import { PdfDocumentBuilder, type EmpresaInfo, type DocumentConfig, type FieldItem } from './pdfDocumentEngine';
import { ParteTrabajo } from '@/contexts/PartesTrabajoContext';
import { loadEmpresaYConfigParaPdf } from './pdfBranding';

// Forms of payment that mean "no charge" — entire payment section hidden.
const FORMA_PAGO_SIN_CARGO = new Set([
  'no_aplica', 'noaplica', 'sin_cargo', 'sin-cargo', 'incluido',
  'gratis', 'cortesia', 'cortesía', 'garantia', 'garantía',
]);

const FORMA_PAGO_LABELS: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia bancaria',
  domiciliacion: 'Domiciliación',
  domiciliación: 'Domiciliación',
  tarjeta: 'Tarjeta',
  bizum: 'Bizum',
  cheque: 'Cheque',
  pendiente: 'Pendiente de pago',
};

function shouldShowPagoSection(parte: ParteTrabajo): boolean {
  const fp = (parte.forma_pago || '').toString().trim().toLowerCase();
  if (!fp) return false;
  if (FORMA_PAGO_SIN_CARGO.has(fp)) return false;
  // If amount is 0/null AND payment method is efectivo, hide — nothing meaningful to show.
  const importe = Number(parte.importe_sin_iva ?? parte.importe_con_iva ?? 0);
  if (fp === 'efectivo' && (!importe || importe <= 0)) return false;
  return true;
}

export const generateParteTrabajoePdf = async (
  parte: ParteTrabajo,
  clienteNombre: string,
  tecnicoNombre: string,
  clienteCompleto?: any,
  empresa?: EmpresaInfo | null,
  docConfig?: Partial<DocumentConfig>,
) => {
  const builder = await PdfDocumentBuilder.create(empresa, docConfig);

  const fechaEmision = new Date().toLocaleDateString('es-ES');

  // ── Header con marca ──
  builder.addBrandedHeader({
    titulo: 'Parte de Trabajo',
    numero: parte.numero_parte ? `Nº ${parte.numero_parte}` : undefined,
    estado: getEstadoText(parte.estado),
    fechaEmision: `Emitido: ${fechaEmision}`,
  });

  // ── Cliente + Lugar de intervención (dos tarjetas) ──
  const clienteFields: FieldItem[] = [
    { label: 'Razón social', value: clienteNombre },
  ];
  if (clienteCompleto?.nif_cif) clienteFields.push({ label: 'NIF/CIF', value: clienteCompleto.nif_cif });
  if (clienteCompleto?.telefono) clienteFields.push({ label: 'Teléfono', value: clienteCompleto.telefono });
  if (clienteCompleto?.email) clienteFields.push({ label: 'Email', value: clienteCompleto.email });

  const dirOverride = (parte as any).direccion_intervencion;
  const cpOverride = (parte as any).codigo_postal_intervencion;
  const pobOverride = (parte as any).poblacion_intervencion;
  const direccion = dirOverride || clienteCompleto?.direccion;
  const poblacion = pobOverride || clienteCompleto?.poblacion;
  const codigoPostal = cpOverride || clienteCompleto?.codigo_postal;

  const lugarFields: FieldItem[] = [];
  if (direccion) lugarFields.push({ label: 'Dirección', value: direccion });
  if (poblacion) lugarFields.push({ label: 'Población', value: codigoPostal ? `${poblacion} (${codigoPostal})` : poblacion });
  else if (codigoPostal) lugarFields.push({ label: 'C.P.', value: codigoPostal });
  if (lugarFields.length === 0) lugarFields.push({ label: '—', value: 'No especificada' });

  builder.addInfoCardRow(
    { title: 'Cliente', fields: clienteFields },
    { title: dirOverride || pobOverride || cpOverride ? 'Lugar de la intervención' : 'Dirección', fields: lugarFields },
  );

  // ── Datos del trabajo (tarjeta única con grid) ──
  const fechaTrabajo = new Date(parte.fecha_trabajo).toLocaleDateString('es-ES');
  const trabajoFields: FieldItem[] = [
    { label: 'Fecha', value: fechaTrabajo },
    { label: 'Hora inicio', value: parte.hora_inicio || '—' },
    { label: 'Hora fin', value: parte.hora_fin || '—' },
    { label: 'Duración', value: parte.duracion_total ? formatDuration(parte.duracion_total) : '—' },
    { label: 'Tipo', value: getTipoIntervencionText(parte.tipo_intervencion) },
    { label: 'Técnico', value: tecnicoNombre },
  ];
  if (parte.tiene_desplazamiento) trabajoFields.push({ label: 'Desplazamiento', value: 'Sí' });
  builder.addInfoCard('Datos del trabajo', trabajoFields);

  // ── Descripción ──
  if (parte.descripcion_trabajo?.trim()) {
    builder.addSection('Descripción del trabajo');
    builder.addQuoteBlock(parte.descripcion_trabajo);
  }

  // ── Materiales / resultado (si existen) ──
  const extras: FieldItem[] = [];
  if ((parte as any).materiales_necesarios) extras.push({ label: 'Materiales', value: (parte as any).materiales_necesarios });
  if ((parte as any).resultado_intervencion) extras.push({ label: 'Resultado', value: getResultadoText((parte as any).resultado_intervencion) });
  if ((parte as any).motivo_no_resuelto) extras.push({ label: 'Motivo', value: (parte as any).motivo_no_resuelto });
  if (extras.length > 0) {
    builder.addInfoCard('Detalles adicionales', extras);
  }

  // ── Pago (solo si aplica) ──
  if (shouldShowPagoSection(parte)) {
    builder.addSection('Importe');
    const fp = (parte.forma_pago || '').toString().toLowerCase();
    const fpLabel = FORMA_PAGO_LABELS[fp] || (fp.charAt(0).toUpperCase() + fp.slice(1));

    const importe = Number(parte.importe_sin_iva ?? 0);
    if (fp === 'efectivo' && importe > 0) {
      const ivaPct = Number(parte.iva_porcentaje ?? 0);
      const ivaAmount = (importe * ivaPct) / 100;
      const total = Number(parte.importe_con_iva ?? importe + ivaAmount);
      const items = [
        { label: 'Forma de pago', value: fpLabel },
        { label: 'Subtotal', value: `${importe.toFixed(2)} €` },
      ];
      if (ivaPct > 0) items.push({ label: `IVA (${ivaPct}%)`, value: `${ivaAmount.toFixed(2)} €` });
      builder.addTotalsBox(items, { label: 'Total', value: `${total.toFixed(2)} €` });
    } else {
      // Forma de pago sin importe: línea discreta
      builder.addField('Forma de pago', fpLabel);
      builder.addSpacer(3);
    }
  }

  // ── Firmas ──
  builder.addSignatureBlock([
    { label: 'Firma del Técnico', imageData: parte.firma_tecnico || null, nombre: tecnicoNombre },
    { label: 'Sello de la empresa', esEmpresa: true, nombre: '' },
    { label: 'Firma del Cliente', imageData: parte.firma_cliente || null, nombre: clienteNombre },
  ]);

  return builder;
};

const formatDuration = (duration: unknown): string => {
  if (!duration) return 'No especificada';
  try {
    if (typeof duration === 'string') {
      const match = duration.match(/(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [, hours, minutes] = match;
        return `${parseInt(hours)}h ${parseInt(minutes)}m`;
      }
    }
    return duration.toString();
  } catch {
    return 'Formato no válido';
  }
};

const getEstadoText = (estado: string): string => {
  switch (estado) {
    case 'borrador': return 'Borrador';
    case 'finalizado': return 'Finalizado';
    case 'facturado': return 'Facturado';
    default: return estado;
  }
};

const getTipoIntervencionText = (tipo: string): string => {
  switch (tipo) {
    case 'revision_sistema_seguridad': return 'Revisión sistema de seguridad';
    case 'averias': return 'Averías';
    case 'auditoria': return 'Auditoría';
    case 'entrega_materiales': return 'Entrega materiales';
    case 'otros': return 'Otros';
    default: return tipo;
  }
};

const getResultadoText = (r: string): string => {
  switch (r) {
    case 'resuelto': return 'Resuelto';
    case 'no_resuelto': return 'No resuelto';
    case 'no_realizado': return 'No realizado';
    default: return r;
  }
};

export const downloadParteTrabajoePdf = async (
  parte: ParteTrabajo,
  clienteNombre: string,
  tecnicoNombre: string,
  clienteCompleto?: any,
  empresa?: EmpresaInfo | null,
  docConfig?: Partial<DocumentConfig>,
) => {
  // Auto-load empresa+config if no se pasaron
  let resolvedEmpresa = empresa;
  let resolvedConfig = docConfig;
  if (!resolvedEmpresa && (parte as any).empresa_id) {
    const bundle = await loadEmpresaYConfigParaPdf((parte as any).empresa_id);
    resolvedEmpresa = bundle.empresa;
    resolvedConfig = resolvedConfig || bundle.docConfig;
  }
  const builder = await generateParteTrabajoePdf(parte, clienteNombre, tecnicoNombre, clienteCompleto, resolvedEmpresa, resolvedConfig);
  const filename = `parte_trabajo_${parte.numero_parte || parte.id}.pdf`;
  builder.save(filename);
};

export const openParteTrabajoePdf = async (
  parte: ParteTrabajo,
  clienteNombre: string,
  tecnicoNombre: string,
  clienteCompleto?: any,
  empresa?: EmpresaInfo | null,
  docConfig?: Partial<DocumentConfig>,
) => {
  let resolvedEmpresa = empresa;
  let resolvedConfig = docConfig;
  if (!resolvedEmpresa && (parte as any).empresa_id) {
    const bundle = await loadEmpresaYConfigParaPdf((parte as any).empresa_id);
    resolvedEmpresa = bundle.empresa;
    resolvedConfig = resolvedConfig || bundle.docConfig;
  }
  const builder = await generateParteTrabajoePdf(parte, clienteNombre, tecnicoNombre, clienteCompleto, resolvedEmpresa, resolvedConfig);
  const blob = builder.finalize().output('blob') as Blob;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  if (!win) {
    // Popup bloqueado: forzar descarga como fallback
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

